import { Pie } from '@ant-design/plots';
import { Card, Divider, Select, Typography } from 'antd';
import { FC, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { CHART_COLORS, getProgressBarShadow } from '../../../constants/styles';
import {
  DonationGoalData,
  DonationRecord,
} from '../../../types/phlebotomist-dashboard';
import { DeltaIndicator } from '../../charts/DeltaIndicator';
import { DonationGoalGauge } from '../../charts/DonationGoalGauge';
import { PhlebTooltip } from '../../PhlebTooltip';

const { Text } = Typography;

interface IAttemptedDonationsCardProps {
  attempted: number;
  singleDonors: number;
  doubleDonors: number;
  successfulDonations: number;
  successfulSingleDonors: number;
  successfulDoubleDonors: number;
  pendingDonations?: number;
  loading?: boolean;
  /** Measured pane width from parent - used for responsive calculations */
  paneWidth?: number;
  /** Window width for responsive layout decisions */
  windowWidth?: number;
  /** Whether we're in split screen mode */
  isSplitMode?: boolean;
  /** Comparison data for inline compare mode */
  comparisonData?: {
    attempted: number;
    singleDonors: number;
    doubleDonors: number;
    successfulDonations: number;
    successfulSingleDonors: number;
    successfulDoubleDonors: number;
  };
  /** Current date range label for legend (inline mode) */
  currentDateRangeLabel?: string;
  /** Comparison date range label for legend (inline mode) */
  comparisonDateRangeLabel?: string;
  /** Goal data for showing progress gauge (only shown when specific phlebotomist selected) */
  goalData?: DonationGoalData | null;
  /** Comparison goal data for inline mode */
  comparisonGoalData?: DonationGoalData | null;
  /** Phlebotomist name for goal gauge title (admin/MD viewing specific phlebotomist) */
  phlebotomistName?: string;
  /** Donation records for location/donor success rate breakdown */
  donations?: DonationRecord[];
  /** Show debug information */
  showDebug?: boolean;
  /** Previous period data for trend arrows (only in normal/default mode) */
  previousPeriodData?: {
    attempted: number;
    singleDonors: number;
    doubleDonors: number;
    successfulDonations: number;
    successfulSingleDonors: number;
    successfulDoubleDonors: number;
    pendingDonations?: number;
  };
}

export const AttemptedDonationsCard: FC<IAttemptedDonationsCardProps> = ({
  attempted,
  singleDonors,
  doubleDonors,
  successfulDonations,
  successfulSingleDonors,
  successfulDoubleDonors,
  pendingDonations = 0,
  loading = false,
  paneWidth,
  isSplitMode = false,
  comparisonData,
  currentDateRangeLabel,
  comparisonDateRangeLabel,
  goalData,
  comparisonGoalData,
  phlebotomistName,
  donations = [],
  showDebug,
  previousPeriodData,
}) => {
  const { t } = useTranslation('dashboard.PhlebotomistDashboard');

  // Location/Donor filter state for success rate filtering
  const [selectedLocation, setSelectedLocation] = useState<string>('all');
  const [selectedDonor, setSelectedDonor] = useState<string>('all');

  // Group donations by location
  const locationData = useMemo(() => {
    const byLocation = new Map<string, DonationRecord[]>();
    donations.forEach((donation) => {
      const key = donation.locationName || 'Unknown';
      if (!byLocation.has(key)) byLocation.set(key, []);
      const arr = byLocation.get(key);
      if (arr) arr.push(donation);
    });
    return Array.from(byLocation.entries())
      .map(([locationName, locationDonations]) => ({
        locationName,
        donations: locationDonations,
        total: locationDonations.length,
      }))
      .sort((a, b) => b.total - a.total);
  }, [donations]);

  // Donations filtered by selected location
  const locationFilteredDonations = useMemo(() => {
    if (selectedLocation === 'all') return donations;
    const loc = locationData.find((l) => l.locationName === selectedLocation);
    return loc?.donations || [];
  }, [selectedLocation, locationData, donations]);

  // Group location-filtered donations by donor
  const donorData = useMemo(() => {
    const byDonor = new Map<
      string,
      { donorName: string; donations: DonationRecord[] }
    >();
    locationFilteredDonations.forEach((donation) => {
      const key = donation.donorId;
      if (!byDonor.has(key))
        byDonor.set(key, { donorName: donation.donorName, donations: [] });
      const entry = byDonor.get(key);
      if (entry) entry.donations.push(donation);
    });
    return Array.from(byDonor.entries())
      .map(([donorId, data]) => ({
        donorId,
        donorName: data.donorName,
        total: data.donations.length,
        donations: data.donations,
      }))
      .sort((a, b) => b.total - a.total);
  }, [locationFilteredDonations]);

  // Final filtered donations (by both location and donor)
  const filteredDonations = useMemo(() => {
    if (selectedDonor === 'all') return locationFilteredDonations;
    const donor = donorData.find((d) => d.donorId === selectedDonor);
    return donor?.donations || [];
  }, [selectedDonor, donorData, locationFilteredDonations]);

  // Location dropdown options
  const locationOptions = useMemo(
    () => [
      { value: 'all', label: t('pokeSuccess.allLocations', 'All Locations') },
      ...locationData.map((loc) => ({
        value: loc.locationName,
        label: `${loc.locationName} (${loc.total})`,
      })),
    ],
    [locationData, t]
  );

  // Donor dropdown options
  const donorOptions = useMemo(
    () => [
      { value: 'all', label: t('pokeSuccess.allDonors', 'All Donors') },
      ...donorData.map((donor) => ({
        value: donor.donorId,
        label: `${donor.donorName} (${donor.total})`,
      })),
    ],
    [donorData, t]
  );

  // Reset donor selection when location changes (since donor list changes)
  const handleLocationChange = (value: string) => {
    setSelectedLocation(value);
    setSelectedDonor('all');
  };

  // Use compact layout when pane width <= 500px
  // Default to non-compact if paneWidth not provided
  const isCompact = paneWidth !== undefined ? paneWidth <= 500 : false;

  // Calculate ring size based on the number of digits in the current value
  // This ensures large numbers don't overflow the center of the ring
  const ringSizing = useMemo(() => {
    const formattedLength = attempted.toLocaleString().length;
    // Also consider comparison number if present
    const compLength = comparisonData
      ? comparisonData.attempted.toLocaleString().length
      : 0;
    const maxLength = Math.max(formattedLength, compLength);
    const hasComparison = !!comparisonData;

    // When comparison is shown, we need more vertical space for two numbers
    // Calculate based on: digits * approximate char width + padding
    // Inner ring needs to fit: main number + comparison number + some padding

    if (isCompact) {
      // Compact mode sizing - smaller base sizes
      if (hasComparison) {
        // Need extra space for stacked numbers
        if (maxLength <= 3) {
          return { outer: 120, inner: 90, fontSize: 18, compFontSize: 12 };
        }
        if (maxLength <= 5) {
          return { outer: 140, inner: 105, fontSize: 16, compFontSize: 11 };
        }
        if (maxLength <= 7) {
          return { outer: 160, inner: 120, fontSize: 14, compFontSize: 10 };
        }
        return { outer: 180, inner: 135, fontSize: 12, compFontSize: 9 };
      }
      // No comparison - more space for single number
      if (maxLength <= 4) {
        return { outer: 120, inner: 90, fontSize: 20, compFontSize: 13 };
      }
      if (maxLength <= 6) {
        return { outer: 130, inner: 98, fontSize: 16, compFontSize: 11 };
      }
      return { outer: 150, inner: 113, fontSize: 14, compFontSize: 10 };
    }

    // Normal mode sizing
    if (hasComparison) {
      // Need extra space for stacked numbers
      // Approximate: each digit ~10px wide at fontSize 24, plus commas
      if (maxLength <= 4) {
        return { outer: 180, inner: 135, fontSize: 24, compFontSize: 14 };
      }
      if (maxLength <= 5) {
        return { outer: 200, inner: 150, fontSize: 22, compFontSize: 13 };
      }
      if (maxLength <= 6) {
        return { outer: 220, inner: 165, fontSize: 20, compFontSize: 12 };
      }
      if (maxLength <= 7) {
        return { outer: 240, inner: 180, fontSize: 18, compFontSize: 11 };
      }
      return { outer: 260, inner: 195, fontSize: 16, compFontSize: 10 };
    }

    // No comparison - more space for single number
    if (maxLength <= 5) {
      return { outer: 180, inner: 135, fontSize: 28, compFontSize: 16 };
    }
    if (maxLength <= 7) {
      return { outer: 200, inner: 150, fontSize: 22, compFontSize: 14 };
    }
    return { outer: 220, inner: 165, fontSize: 18, compFontSize: 12 };
  }, [attempted, comparisonData, isCompact]);

  const chartData = useMemo(
    () => [
      {
        type: t(
          'cards.attemptedDonations.singleDonors',
          'SGL Donors (250-419mL)'
        ),
        value: singleDonors,
      },
      {
        type: t('cards.attemptedDonations.doubleDonors', 'DBL Donors (420+mL)'),
        value: doubleDonors,
      },
    ],
    [singleDonors, doubleDonors, t]
  );

  // Color mapping for ring chart segments
  const sglLabel = t(
    'cards.attemptedDonations.singleDonors',
    'SGL Donors (250-419mL)'
  );
  const dblLabel = t(
    'cards.attemptedDonations.doubleDonors',
    'DBL Donors (420+mL)'
  );

  const config = useMemo(
    () => ({
      data: chartData,
      angleField: 'value',
      colorField: 'type',
      innerRadius: 0.75,
      radius: 1,
      legend: false as const,
      tooltip: false as const,
      label: false as const,
      statistic: {
        title: false as const,
        content: false as const,
      },
      scale: {
        color: {
          domain: [sglLabel, dblLabel],
          range: [
            CHART_COLORS.successRates.sglDonors,
            CHART_COLORS.successRates.dblDonors,
          ],
        },
      },
    }),
    [chartData, sglLabel, dblLabel]
  );

  // Comparison chart data
  const compChartData = useMemo(
    () =>
      comparisonData
        ? [
            {
              type: t(
                'cards.attemptedDonations.singleDonors',
                'SGL Donors (250-419mL)'
              ),
              value: comparisonData.singleDonors,
            },
            {
              type: t(
                'cards.attemptedDonations.doubleDonors',
                'DBL Donors (420+mL)'
              ),
              value: comparisonData.doubleDonors,
            },
          ]
        : [],
    [comparisonData, t]
  );

  const compConfig = useMemo(
    () => ({
      data: compChartData,
      angleField: 'value',
      colorField: 'type',
      innerRadius: 0.7,
      radius: 1,
      legend: false as const,
      tooltip: false as const,
      label: false as const,
      statistic: {
        title: false as const,
        content: false as const,
      },
      scale: {
        color: {
          domain: [sglLabel, dblLabel],
          range: ['#a6a6a6', '#737373'], // Greyscale for comparison ring
        },
      },
    }),
    [compChartData, sglLabel, dblLabel]
  );

  return (
    <Card
      title={t('cards.attemptedDonations.title', 'Donation Collection')}
      loading={loading}
      style={{ height: '100%' }}
      headStyle={{ fontSize: 18 }}
      bodyStyle={{
        height: 'calc(100% - 57px)',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Subtitle: Attempted Donations */}
      <Text
        strong
        style={{
          display: 'block',
          textAlign: 'center',
          marginBottom: isCompact ? 4 : 8,
          fontSize: isCompact ? 13 : 15,
          color: '#595959',
        }}
      >
        <PhlebTooltip text="Total number of donation draws attempted during the selected period, broken down by donor type (SGL vs DBL). Each bar represents the count of scheduled draws that were started, regardless of outcome.">
          {t('cards.attemptedDonations.subtitle', 'Attempted Donations')}
        </PhlebTooltip>
      </Text>
      {/* Legend - centered below subtitle */}
      {/* Stack legend groups vertically when paneWidth < 768 */}
      <div
        style={{
          display: 'flex',
          flexDirection:
            paneWidth !== undefined && paneWidth < 768 ? 'column' : 'row',
          alignItems: 'center',
          justifyContent: 'center',
          gap: paneWidth !== undefined && paneWidth < 768 ? 4 : 16,
          marginBottom: isCompact ? 8 : 12,
          fontSize: 12,
        }}
      >
        {comparisonData && currentDateRangeLabel && comparisonDateRangeLabel ? (
          <>
            {/* Current period row */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
              <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                <span
                  style={{
                    display: 'inline-block',
                    width: 10,
                    height: 10,
                    backgroundColor: CHART_COLORS.successRates.sglDonors,
                    borderRadius: 2,
                  }}
                />
                <span style={{ marginLeft: 2 }}>
                  {t('legend.sglDonors', 'SGL Donors')}
                </span>
              </span>
              <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                <span
                  style={{
                    display: 'inline-block',
                    width: 10,
                    height: 10,
                    backgroundColor: CHART_COLORS.successRates.dblDonors,
                    borderRadius: 2,
                  }}
                />
                <span style={{ marginLeft: 2 }}>
                  {t('legend.dblDonors', 'DBL Donors')}
                </span>
              </span>
              <span style={{ color: '#595959' }}>
                ({currentDateRangeLabel})
              </span>
            </div>
            {/* Separator - vertical line for row layout, hidden for stacked */}
            {paneWidth !== undefined && paneWidth >= 768 && (
              <span style={{ borderLeft: '1px solid #d9d9d9', height: 16 }} />
            )}
            {/* Comparison period row */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
              <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                <span
                  style={{
                    display: 'inline-block',
                    width: 10,
                    height: 10,
                    backgroundColor: '#a6a6a6',
                    borderRadius: 2,
                  }}
                />
                <span style={{ marginLeft: 2, color: '#626262' }}>
                  {t('legend.sglDonors', 'SGL Donors')}
                </span>
              </span>
              <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                <span
                  style={{
                    display: 'inline-block',
                    width: 10,
                    height: 10,
                    backgroundColor: '#737373',
                    borderRadius: 2,
                  }}
                />
                <span style={{ marginLeft: 2, color: '#626262' }}>
                  {t('legend.dblDonors', 'DBL Donors')}
                </span>
              </span>
              <span style={{ color: '#626262' }}>
                ({comparisonDateRangeLabel})
              </span>
            </div>
          </>
        ) : (
          <>
            {/* Standard legend: SGL Donors / DBL Donors */}
            <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <span
                style={{
                  display: 'inline-block',
                  width: 10,
                  height: 10,
                  backgroundColor: CHART_COLORS.successRates.sglDonors,
                  borderRadius: 2,
                }}
              />
              <span style={{ marginLeft: 2 }}>
                {t('legend.sglDonors', 'SGL Donors')}
              </span>
            </span>
            <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <span
                style={{
                  display: 'inline-block',
                  width: 10,
                  height: 10,
                  backgroundColor: CHART_COLORS.successRates.dblDonors,
                  borderRadius: 2,
                }}
              />
              <span style={{ marginLeft: 2 }}>
                {t('legend.dblDonors', 'DBL Donors')}
              </span>
            </span>
          </>
        )}
      </div>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: isCompact ? 16 : 24,
        }}
      >
        {/* Ring Chart */}
        <div
          style={{
            position: 'relative',
            width: ringSizing.outer,
            height: ringSizing.outer,
          }}
        >
          {/* Main ring chart */}
          <Pie {...config} width={ringSizing.outer} height={ringSizing.outer} />
          {/* Comparison ring chart (overlaid, thinner, closer to outer ring) */}
          {comparisonData && (
            <div
              style={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                width: ringSizing.inner,
                height: ringSizing.inner,
              }}
            >
              <Pie
                {...compConfig}
                width={ringSizing.inner}
                height={ringSizing.inner}
              />
            </div>
          )}
          {/* Center content */}
          <div
            style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              textAlign: 'center',
            }}
          >
            <div
              style={{
                fontSize: ringSizing.fontSize,
                fontWeight: 700,
                lineHeight: 1,
              }}
            >
              {attempted.toLocaleString()}
            </div>
            {previousPeriodData && !comparisonData && (
              <div style={{ marginTop: -2 }}>
                <DeltaIndicator
                  current={attempted}
                  comparison={previousPeriodData.attempted}
                  higherIsBetter
                  showZero
                  size="small"
                />
              </div>
            )}
            {/* Comparison number below */}
            {comparisonData && (
              <div
                style={{
                  fontSize: ringSizing.compFontSize,
                  color: '#626262',
                  marginTop: 0,
                }}
              >
                {comparisonData.attempted.toLocaleString()}
              </div>
            )}
          </div>
        </div>

        {/* Legend with values */}
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: isCompact ? 8 : 16,
          }}
        >
          <div>
            <Text
              type="secondary"
              style={{ fontSize: isCompact ? 11 : 12, display: 'block' }}
            >
              {t(
                'cards.attemptedDonations.singleDonors',
                'SGL Donors (250-419mL)'
              )}
            </Text>
            {/* Comparison value above - tighter gap */}
            {comparisonData && (
              <div
                style={{
                  fontSize: isCompact ? 13 : 16,
                  color: '#a6a6a6', // Match SGL comparison ring color
                  marginBottom: -4,
                  lineHeight: 1,
                }}
              >
                {comparisonData.singleDonors.toLocaleString()}
              </div>
            )}
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 4 }}>
              <Text
                strong
                style={{
                  fontSize: isCompact ? 18 : 24,
                  color: '#91b3e8',
                }}
              >
                {singleDonors}
              </Text>
              {comparisonData ? (
                <DeltaIndicator
                  current={singleDonors}
                  comparison={comparisonData.singleDonors}
                  higherIsBetter
                  size={isCompact ? 'small' : 'medium'}
                />
              ) : previousPeriodData ? (
                <DeltaIndicator
                  current={singleDonors}
                  comparison={previousPeriodData.singleDonors}
                  higherIsBetter
                  showZero
                  size="small"
                />
              ) : null}
            </div>
          </div>
          <div>
            <Text
              type="secondary"
              style={{ fontSize: isCompact ? 11 : 12, display: 'block' }}
            >
              {t(
                'cards.attemptedDonations.doubleDonors',
                'DBL Donors (420+mL)'
              )}
            </Text>
            {/* Comparison value above - tighter gap */}
            {comparisonData && (
              <div
                style={{
                  fontSize: isCompact ? 13 : 16,
                  color: '#737373', // Match DBL comparison ring color
                  marginBottom: -4,
                  lineHeight: 1,
                }}
              >
                {comparisonData.doubleDonors.toLocaleString()}
              </div>
            )}
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 4 }}>
              <Text
                strong
                style={{
                  fontSize: isCompact ? 18 : 24,
                  color: CHART_COLORS.successRates.dblDonors,
                }}
              >
                {doubleDonors}
              </Text>
              {comparisonData ? (
                <DeltaIndicator
                  current={doubleDonors}
                  comparison={comparisonData.doubleDonors}
                  higherIsBetter
                  size={isCompact ? 'small' : 'medium'}
                />
              ) : previousPeriodData ? (
                <DeltaIndicator
                  current={doubleDonors}
                  comparison={previousPeriodData.doubleDonors}
                  higherIsBetter
                  showZero
                  size="small"
                />
              ) : null}
            </div>
          </div>
        </div>
      </div>

      <Divider style={{ margin: '12px 0 0 0' }} />

      {/* Bottom section: 2-column layout (Goal Gauge | Total stats) */}
      {/* Uses flex-wrap to automatically stack when container is too narrow */}
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: isCompact ? 8 : 16,
          flex: 1,
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {/* Left column: Goal Gauge - shown when specific phlebotomist selected */}
        {goalData && (
          <div
            style={{
              flex: '1 1 200px',
              minWidth: 200,
              display: 'flex',
              justifyContent: 'center',
              marginTop: isCompact ? 8 : 12,
            }}
          >
            <DonationGoalGauge
              goalData={goalData}
              successfulDonations={successfulDonations}
              dateRangeLabel={currentDateRangeLabel}
              isCompact={isCompact}
              comparisonSuccessfulDonations={
                comparisonData?.successfulDonations
              }
              comparisonGoalData={comparisonGoalData}
              comparisonDateRangeLabel={comparisonDateRangeLabel}
              phlebotomistName={phlebotomistName}
            />
          </div>
        )}

        {/* Right column: Total stats with SGL/DBL breakdown */}
        <div
          style={{
            flex: '1 1 200px',
            minWidth: 200,
            display: 'flex',
            justifyContent: 'center',
            // When goalData is not visible, add same top margin as Goal Gauge would have
            marginTop: goalData ? 0 : isCompact ? 8 : 12,
          }}
        >
          {(() => {
            // Calculate success rates from filtered donations when filters are active
            const hasFilters = donations.length > 0;
            const useFilteredRates =
              hasFilters &&
              (selectedLocation !== 'all' || selectedDonor !== 'all');

            // Always use donation records to calculate rates (properly excludes Pending per SGL/DBL)
            // When location/donor filters are active, use filteredDonations; otherwise use all donations
            const ratesSource = useFilteredRates
              ? filteredDonations
              : donations;
            const calcRates = (source: DonationRecord[]) => {
              const eligible = source.filter(
                (d) => d.status !== 'Ineligible' && d.status !== 'Pending'
              );
              const successful = eligible.filter((d) => d.status !== 'Failed');
              const sglEligible = eligible.filter((d) => d.type === 'Single');
              const sglSuccessful = sglEligible.filter(
                (d) => d.status !== 'Failed'
              );
              const dblEligible = eligible.filter((d) => d.type === 'Double');
              const dblSuccessful = dblEligible.filter(
                (d) => d.status !== 'Failed'
              );
              return {
                total:
                  eligible.length > 0
                    ? Math.round((successful.length / eligible.length) * 1000) /
                      10
                    : 0,
                sgl:
                  sglEligible.length > 0
                    ? Math.round(
                        (sglSuccessful.length / sglEligible.length) * 1000
                      ) / 10
                    : 0,
                dbl:
                  dblEligible.length > 0
                    ? Math.round(
                        (dblSuccessful.length / dblEligible.length) * 1000
                      ) / 10
                    : 0,
              };
            };

            // Use donation-record-based rates when available, fall back to props
            const donationRates = hasFilters ? calcRates(ratesSource) : null;

            // Exclude pending donations from success rate denominators (prop fallback)
            const adjustedAttempted = attempted - pendingDonations;

            const totalSuccessPercent = donationRates
              ? donationRates.total
              : adjustedAttempted > 0
              ? Math.round((successfulDonations / adjustedAttempted) * 1000) /
                10
              : 0;

            const sglSuccessPercent = donationRates
              ? donationRates.sgl
              : singleDonors > 0
              ? Math.round((successfulSingleDonors / singleDonors) * 1000) / 10
              : 0;

            const dblSuccessPercent = donationRates
              ? donationRates.dbl
              : doubleDonors > 0
              ? Math.round((successfulDoubleDonors / doubleDonors) * 1000) / 10
              : 0;

            // Previous period success percentages (for trend arrows)
            const prevTotalSuccessPercent =
              previousPeriodData && !comparisonData
                ? (() => {
                    const prevPending =
                      previousPeriodData.pendingDonations ?? 0;
                    const prevAdj = previousPeriodData.attempted - prevPending;
                    return prevAdj > 0
                      ? Math.round(
                          (previousPeriodData.successfulDonations / prevAdj) *
                            1000
                        ) / 10
                      : 0;
                  })()
                : undefined;

            const prevSglSuccessPercent =
              previousPeriodData && !comparisonData
                ? previousPeriodData.singleDonors > 0
                  ? Math.round(
                      (previousPeriodData.successfulSingleDonors /
                        previousPeriodData.singleDonors) *
                        1000
                    ) / 10
                  : 0
                : undefined;

            const prevDblSuccessPercent =
              previousPeriodData && !comparisonData
                ? previousPeriodData.doubleDonors > 0
                  ? Math.round(
                      (previousPeriodData.successfulDoubleDonors /
                        previousPeriodData.doubleDonors) *
                        1000
                    ) / 10
                  : 0
                : undefined;

            // Comparison percentages (not affected by location/donor filters)
            const compTotalSuccessPercent = comparisonData
              ? comparisonData.attempted > 0
                ? Math.round(
                    (comparisonData.successfulDonations /
                      comparisonData.attempted) *
                      1000
                  ) / 10
                : 0
              : undefined;

            const compSglSuccessPercent = comparisonData
              ? comparisonData.singleDonors > 0
                ? Math.round(
                    (comparisonData.successfulSingleDonors /
                      comparisonData.singleDonors) *
                      1000
                  ) / 10
                : 0
              : undefined;

            const compDblSuccessPercent = comparisonData
              ? comparisonData.doubleDonors > 0
                ? Math.round(
                    (comparisonData.successfulDoubleDonors /
                      comparisonData.doubleDonors) *
                      1000
                  ) / 10
                : 0
              : undefined;

            return (
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  width: '85%',
                }}
              >
                {/* Simple title - matching subtitle style */}
                <Text
                  strong
                  style={{
                    fontSize: isCompact ? 13 : 15,
                    color: '#595959',
                    display: 'block',
                    textAlign: 'center',
                    marginBottom: isCompact ? 8 : 12,
                  }}
                >
                  <PhlebTooltip text="Percentage of attempted donations that produced at least one non-discarded product. Total = all donors; SGL/DBL break down by donor type. A donation is 'successful' if any product unit was collected without being discarded.">
                    {t(
                      'cards.attemptedDonations.successRates',
                      'Overall Donation Success Rates'
                    )}
                  </PhlebTooltip>
                </Text>

                {/* Pending donations notice - always show in split mode */}
                {(pendingDonations > 0 || isSplitMode) && (
                  <Text
                    type="secondary"
                    style={{
                      display: 'block',
                      textAlign: 'center',
                      fontSize: isCompact ? 10 : 12,
                      marginBottom: isCompact ? 4 : 8,
                      fontStyle: 'italic',
                    }}
                  >
                    -- {pendingDonations} Donation
                    {pendingDonations !== 1 ? 's' : ''} pending processing --
                  </Text>
                )}

                {/* Location/Donor filter dropdowns */}
                {hasFilters && (
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: isCompact ? 4 : 6,
                      width: '100%',
                      marginBottom: isCompact ? 8 : 12,
                    }}
                  >
                    <Select
                      value={selectedLocation}
                      onChange={handleLocationChange}
                      options={locationOptions}
                      size={isCompact ? 'small' : 'middle'}
                      style={{ width: '100%' }}
                      popupMatchSelectWidth={false}
                    />
                    <Select
                      value={selectedDonor}
                      onChange={setSelectedDonor}
                      options={donorOptions}
                      size={isCompact ? 'small' : 'middle'}
                      style={{ width: '100%' }}
                      popupMatchSelectWidth={false}
                      showSearch
                      filterOption={(input, option) =>
                        (option?.label ?? '')
                          .toLowerCase()
                          .includes(input.toLowerCase())
                      }
                    />
                  </div>
                )}

                {/* Stacked progress bars: Total, SGL Donors, DBL Donors */}
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: isCompact ? 8 : 12,
                    width: '100%',
                  }}
                >
                  {/* Total */}
                  <div>
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'baseline',
                        marginBottom: 2,
                      }}
                    >
                      <Text
                        type="secondary"
                        style={{ fontSize: isCompact ? 10 : 12 }}
                      >
                        {t('cards.attemptedDonations.total', 'Total')}
                      </Text>
                      <Text
                        strong
                        style={{
                          fontSize: isCompact ? 12 : 14,
                          color: CHART_COLORS.successRates.overall,
                        }}
                      >
                        {totalSuccessPercent}%
                      </Text>
                    </div>
                    {/* Progress bar */}
                    <div
                      style={{
                        width: '100%',
                        height: isCompact ? 8 : 10,
                        backgroundColor: '#e8e8e8',
                        borderRadius: 4,
                        overflow: 'hidden',
                        boxShadow: getProgressBarShadow(),
                      }}
                    >
                      <div
                        style={{
                          width: `${Math.min(totalSuccessPercent, 100)}%`,
                          height: '100%',
                          backgroundColor: CHART_COLORS.successRates.overall,
                          borderRadius: 4,
                          transition: 'width 0.3s ease',
                        }}
                      />
                    </div>
                    {/* Trend indicator under bar */}
                    {prevTotalSuccessPercent !== undefined && (
                      <div style={{ textAlign: 'right', marginTop: 2 }}>
                        <DeltaIndicator
                          current={totalSuccessPercent}
                          comparison={prevTotalSuccessPercent}
                          higherIsBetter
                          showZero
                          size="small"
                          isPercentage
                        />
                      </div>
                    )}
                    {/* Comparison data for inline mode */}
                    {compTotalSuccessPercent !== undefined && (
                      <>
                        <div
                          style={{
                            display: 'flex',
                            justifyContent: 'flex-end',
                            alignItems: 'baseline',
                            marginTop: 4,
                            marginBottom: 2,
                          }}
                        >
                          <Text
                            style={{
                              fontSize: isCompact ? 10 : 12,
                              color: '#626262',
                            }}
                          >
                            {compTotalSuccessPercent}%
                          </Text>
                        </div>
                        {/* Comparison progress bar - greyed out */}
                        <div
                          style={{
                            width: '100%',
                            height: isCompact ? 6 : 8,
                            backgroundColor: '#e8e8e8',
                            borderRadius: 4,
                            overflow: 'hidden',
                            boxShadow: getProgressBarShadow(),
                          }}
                        >
                          <div
                            style={{
                              width: `${Math.min(
                                compTotalSuccessPercent,
                                100
                              )}%`,
                              height: '100%',
                              backgroundColor: '#a6a6a6',
                              borderRadius: 4,
                              transition: 'width 0.3s ease',
                            }}
                          />
                        </div>
                      </>
                    )}
                  </div>

                  {/* SGL Donors - blue like chart */}
                  <div>
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'baseline',
                        marginBottom: 2,
                      }}
                    >
                      <Text
                        type="secondary"
                        style={{ fontSize: isCompact ? 10 : 12 }}
                      >
                        {t(
                          'cards.attemptedDonations.sglDonorsShort',
                          'SGL Donors'
                        )}
                      </Text>
                      <Text
                        strong
                        style={{
                          fontSize: isCompact ? 12 : 14,
                          color: '#90b4eb',
                        }}
                      >
                        {sglSuccessPercent}%
                      </Text>
                    </div>
                    {/* Progress bar */}
                    <div
                      style={{
                        width: '100%',
                        height: isCompact ? 8 : 10,
                        backgroundColor: '#e8e8e8',
                        borderRadius: 4,
                        overflow: 'hidden',
                        boxShadow: getProgressBarShadow(),
                      }}
                    >
                      <div
                        style={{
                          width: `${Math.min(sglSuccessPercent, 100)}%`,
                          height: '100%',
                          backgroundColor: CHART_COLORS.successRates.sglDonors,
                          borderRadius: 4,
                          transition: 'width 0.3s ease',
                        }}
                      />
                    </div>
                    {/* Trend indicator under bar */}
                    {prevSglSuccessPercent !== undefined && (
                      <div style={{ textAlign: 'right', marginTop: 2 }}>
                        <DeltaIndicator
                          current={sglSuccessPercent}
                          comparison={prevSglSuccessPercent}
                          higherIsBetter
                          showZero
                          size="small"
                          isPercentage
                        />
                      </div>
                    )}
                    {/* Comparison data for inline mode */}
                    {compSglSuccessPercent !== undefined && (
                      <>
                        <div
                          style={{
                            display: 'flex',
                            justifyContent: 'flex-end',
                            alignItems: 'baseline',
                            marginTop: 4,
                            marginBottom: 2,
                          }}
                        >
                          <Text
                            style={{
                              fontSize: isCompact ? 10 : 12,
                              color: '#626262',
                            }}
                          >
                            {compSglSuccessPercent}%
                          </Text>
                        </div>
                        {/* Comparison progress bar - greyed out */}
                        <div
                          style={{
                            width: '100%',
                            height: isCompact ? 6 : 8,
                            backgroundColor: '#e8e8e8',
                            borderRadius: 4,
                            overflow: 'hidden',
                            boxShadow: getProgressBarShadow(),
                          }}
                        >
                          <div
                            style={{
                              width: `${Math.min(compSglSuccessPercent, 100)}%`,
                              height: '100%',
                              backgroundColor: '#a6a6a6',
                              borderRadius: 4,
                              transition: 'width 0.3s ease',
                            }}
                          />
                        </div>
                      </>
                    )}
                  </div>

                  {/* DBL Donors - purple like chart */}
                  <div>
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'baseline',
                        marginBottom: 2,
                      }}
                    >
                      <Text
                        type="secondary"
                        style={{ fontSize: isCompact ? 10 : 12 }}
                      >
                        {t(
                          'cards.attemptedDonations.dblDonorsShort',
                          'DBL Donors'
                        )}
                      </Text>
                      <Text
                        strong
                        style={{
                          fontSize: isCompact ? 12 : 14,
                          color: CHART_COLORS.successRates.dblDonors,
                        }}
                      >
                        {dblSuccessPercent}%
                      </Text>
                    </div>
                    {/* Progress bar */}
                    <div
                      style={{
                        width: '100%',
                        height: isCompact ? 8 : 10,
                        backgroundColor: '#e8e8e8',
                        borderRadius: 4,
                        overflow: 'hidden',
                        boxShadow: getProgressBarShadow(),
                      }}
                    >
                      <div
                        style={{
                          width: `${Math.min(dblSuccessPercent, 100)}%`,
                          height: '100%',
                          backgroundColor: CHART_COLORS.successRates.dblDonors,
                          borderRadius: 4,
                          transition: 'width 0.3s ease',
                        }}
                      />
                    </div>
                    {/* Trend indicator under bar */}
                    {prevDblSuccessPercent !== undefined && (
                      <div style={{ textAlign: 'right', marginTop: 2 }}>
                        <DeltaIndicator
                          current={dblSuccessPercent}
                          comparison={prevDblSuccessPercent}
                          higherIsBetter
                          showZero
                          size="small"
                          isPercentage
                        />
                      </div>
                    )}
                    {/* Comparison data for inline mode */}
                    {compDblSuccessPercent !== undefined && (
                      <>
                        <div
                          style={{
                            display: 'flex',
                            justifyContent: 'flex-end',
                            alignItems: 'baseline',
                            marginTop: 4,
                            marginBottom: 2,
                          }}
                        >
                          <Text
                            style={{
                              fontSize: isCompact ? 10 : 12,
                              color: '#626262',
                            }}
                          >
                            {compDblSuccessPercent}%
                          </Text>
                        </div>
                        {/* Comparison progress bar - greyed out */}
                        <div
                          style={{
                            width: '100%',
                            height: isCompact ? 6 : 8,
                            backgroundColor: '#e8e8e8',
                            borderRadius: 4,
                            overflow: 'hidden',
                            boxShadow: getProgressBarShadow(),
                          }}
                        >
                          <div
                            style={{
                              width: `${Math.min(compDblSuccessPercent, 100)}%`,
                              height: '100%',
                              backgroundColor: '#a6a6a6',
                              borderRadius: 4,
                              transition: 'width 0.3s ease',
                            }}
                          />
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </div>
            );
          })()}
        </div>
      </div>
      {showDebug && !loading && (
        <div
          style={{
            marginTop: 12,
            padding: 8,
            backgroundColor: '#fff5f5',
            border: '1px solid #f31a25',
            borderRadius: 4,
            fontSize: 11,
            fontFamily: 'monospace',
            color: '#f31a25',
          }}
        >
          {(() => {
            const allEligible = donations.filter(
              (d) => d.status !== 'Ineligible' && d.status !== 'Pending'
            );
            const allSuccessful = allEligible.filter(
              (d) => d.status !== 'Failed'
            );
            const sglE = allEligible.filter((d) => d.type === 'Single');
            const sglS = sglE.filter((d) => d.status !== 'Failed');
            const dblE = allEligible.filter((d) => d.type === 'Double');
            const dblS = dblE.filter((d) => d.status !== 'Failed');
            const pendingSgl = donations.filter(
              (d) => d.status === 'Pending' && d.type === 'Single'
            ).length;
            const pendingDbl = donations.filter(
              (d) => d.status === 'Pending' && d.type === 'Double'
            ).length;
            return (
              <>
                <div style={{ fontWeight: 700, marginBottom: 4 }}>
                  DEBUG: Overall Donation Success Rates
                </div>
                <div>Attempted Donations: {attempted}</div>
                <div>
                  Pending Donations: {pendingDonations} (SGL: {pendingSgl}, DBL:{' '}
                  {pendingDbl})
                </div>
                <div>
                  Eligible (excl Pending+Ineligible): {allEligible.length}
                </div>
                <div>Successful Donations: {allSuccessful.length}</div>
                <div>
                  SGL Donors: {sglS.length}/{sglE.length} (
                  {sglE.length > 0
                    ? Math.round((sglS.length / sglE.length) * 1000) / 10
                    : 0}
                  %)
                </div>
                <div>
                  DBL Donors: {dblS.length}/{dblE.length} (
                  {dblE.length > 0
                    ? Math.round((dblS.length / dblE.length) * 1000) / 10
                    : 0}
                  %)
                </div>
                <div>
                  Overall: {allSuccessful.length}/{allEligible.length} ={' '}
                  {allEligible.length > 0
                    ? Math.round(
                        (allSuccessful.length / allEligible.length) * 1000
                      ) / 10
                    : 0}
                  %
                </div>
                {donations.length > 0 && (
                  <>
                    <div>Donation Records: {donations.length}</div>
                    <div>
                      Filtered Donations: {filteredDonations.length}
                      {selectedLocation !== 'all' &&
                        ` (Location: ${selectedLocation})`}
                      {selectedDonor !== 'all' &&
                        ` (Donor: ${
                          donorData.find((d) => d.donorId === selectedDonor)
                            ?.donorName || selectedDonor
                        })`}
                    </div>
                  </>
                )}
              </>
            );
          })()}
        </div>
      )}
    </Card>
  );
};

export default AttemptedDonationsCard;
